import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { nip57 } from 'nostr-tools';
import { 
  Zap, 
  X,
  Loader2,
  Heart,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useNWC } from '@/hooks/useNWCContext';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

const BOOST_AMOUNTS = [21, 100, 500, 1000, 5000, 10000, 50000, 100000];

interface BoostDialogProps {
  track: MusicTrack;
  open: boolean;
  onClose: () => void;
}

export function BoostDialog({ track, open, onClose }: BoostDialogProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [isZapping, setIsZapping] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { getActiveConnection, sendPayment } = useNWC();
  const { toast } = useToast();
  
  const author = useAuthor(track.event.pubkey);
  const metadata = author.data?.metadata;
  const authorEvent = author.data?.event;

  const amount = customAmount ? parseInt(customAmount) : selectedAmount;
  const lnAddress = metadata?.lud16 || metadata?.lud06;

  const handleBoost = async () => {
    if (!amount || amount <= 0 || !lnAddress || !user || !authorEvent) return;

    setIsZapping(true);
    try {
      // Get zap endpoint from author's profile
      const zapEndpoint = await nip57.getZapEndpoint(authorEvent);
      if (!zapEndpoint) {
        toast({
          title: 'Zap endpoint not found',
          description: 'Could not find a zap endpoint for this artist.',
          variant: 'destructive',
        });
        setIsZapping(false);
        return;
      }

      const zapAmount = amount * 1000; // Convert to millisats

      // Create zap request for the artist's profile (not the track)
      const zapRequest = nip57.makeZapRequest({
        profile: track.event.pubkey,
        amount: zapAmount,
        relays: config.relayMetadata.relays.map(r => r.url),
        comment: `Boosting "${track.title}" 🎵`,
      });

      // Sign the zap request
      if (!user.signer) {
        throw new Error('No signer available');
      }
      const signedZapRequest = await user.signer.signEvent(zapRequest);

      // Get invoice from LNURL endpoint
      const res = await fetch(`${zapEndpoint}?amount=${zapAmount}&nostr=${encodeURI(JSON.stringify(signedZapRequest))}`);
      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${responseData.reason || 'Unknown error'}`);
      }

      const invoice = responseData.pr;
      if (!invoice || typeof invoice !== 'string') {
        throw new Error('Lightning service did not return a valid invoice');
      }

      // Pay using NWC
      const connection = getActiveConnection();
      if (!connection?.isConnected) {
        toast({
          title: 'Wallet not connected',
          description: 'Please connect a wallet to send boosts.',
          variant: 'destructive',
        });
        setIsZapping(false);
        return;
      }

      await sendPayment(connection, invoice);

      toast({
        title: 'Boost sent!',
        description: `You sent ${amount} sats to ${track.artist}!`,
      });
      
      // Show thank you animation
      setShowThankYou(true);
      setTimeout(() => {
        setShowThankYou(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Boost failed:', error);
      toast({
        title: 'Boost failed',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsZapping(false);
    }
  };

  const formatAmount = (sats: number) => {
    if (sats >= 1000) {
      return `${(sats / 1000).toFixed(sats % 1000 === 0 ? 0 : 1)}k`;
    }
    return sats.toString();
  };

  // Use portal to ensure dialog renders at the top level
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!mounted) return null;

  const dialogContent = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop - highest z-index to cover everything including player */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            style={{ zIndex: 99999 }}
          />
          
          {/* Dialog Container - uses flexbox for reliable centering */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none"
            style={{ zIndex: 100000 }}
          >
            <div 
              className="relative w-full max-w-md my-auto rounded-3xl overflow-hidden bg-gradient-to-b from-zinc-900 to-black border border-white/10 shadow-2xl pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Thank You Animation */}
              <AnimatePresence>
                {showThankYou && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-b from-orange-500/20 to-pink-500/20 backdrop-blur-sm"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', damping: 10, stiffness: 100 }}
                    >
                      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center mb-6">
                        <Heart className="h-12 w-12 text-white" fill="white" />
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-center"
                    >
                      <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                      <p className="text-muted-foreground">
                        {track.artist} appreciates your support!
                      </p>
                    </motion.div>
                    {/* Confetti-like sparkles */}
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ 
                          opacity: 1, 
                          scale: 0,
                          x: 0,
                          y: 0,
                        }}
                        animate={{ 
                          opacity: 0, 
                          scale: 1,
                          x: (Math.random() - 0.5) * 300,
                          y: (Math.random() - 0.5) * 300,
                        }}
                        transition={{ 
                          duration: 1.5,
                          delay: 0.1 + i * 0.05,
                          ease: 'easeOut',
                        }}
                        className="absolute"
                        style={{ 
                          left: '50%', 
                          top: '40%',
                        }}
                      >
                        <Sparkles className={cn(
                          'h-6 w-6',
                          i % 3 === 0 ? 'text-orange-400' : i % 3 === 1 ? 'text-pink-400' : 'text-yellow-400'
                        )} />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Header */}
              <div className="p-6 pb-4 text-center">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-3 mb-4"
                >
                  <Avatar className="h-12 w-12 ring-2 ring-orange-500/50">
                    <AvatarImage src={metadata?.picture} />
                    <AvatarFallback className="bg-orange-500/20">
                      <Zap className="h-6 w-6 text-orange-400" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <h3 className="font-bold">Boost {track.artist}</h3>
                    <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {track.title}
                    </p>
                  </div>
                </motion.div>
                <p className="text-sm text-muted-foreground">
                  Send some sats to show your appreciation!
                </p>
              </div>

              {/* Amount Selection */}
              <div className="px-6 pb-4">
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {BOOST_AMOUNTS.map((amt) => (
                    <motion.button
                      key={amt}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSelectedAmount(amt);
                        setCustomAmount('');
                      }}
                      className={cn(
                        'py-3 rounded-xl font-medium text-sm transition-all',
                        selectedAmount === amt && !customAmount
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/30'
                          : 'bg-white/5 hover:bg-white/10 border border-white/10'
                      )}
                    >
                      <Zap className="h-3 w-3 inline mr-1" />
                      {formatAmount(amt)}
                    </motion.button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="relative">
                  <Zap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="Custom amount..."
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      if (e.target.value) setSelectedAmount(null);
                    }}
                    className="pl-9 bg-white/5 border-white/10 rounded-xl"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="p-6 pt-2">
                <Button
                  onClick={handleBoost}
                  disabled={!amount || amount <= 0 || isZapping || !lnAddress}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-lg"
                >
                  {isZapping ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Zap className="h-5 w-5 mr-2" fill="currentColor" />
                      Boost {amount ? amount.toLocaleString() : 0} sats
                    </>
                  )}
                </Button>
                {!lnAddress && (
                  <p className="text-xs text-center text-amber-400 mt-2">
                    Artist hasn't set up Lightning payments yet
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Portal to body to ensure it's above all other elements
  return createPortal(dialogContent, document.body);
}
